/**
 * DeliveryLog — the Delivery tab of the Messaging rail: the send log. One row
 * per notification that went out across channels and sources (flow / campaign /
 * ticket-tier / manual / on-site), searchable, filterable, sortable and
 * paginated. Click a row for the per-recipient event history + rendered message.
 *
 * Route: `messaging.delivery` · capability: delivery.read.
 * Data: the live notification-history ops (listMessageHistory / getMessageHistoryDetail).
 */
import { useEffect, useState, type CSSProperties, type ReactNode } from "react";
import { alpha, Btn, Card, Icon, Input, Pill, Popover, T, tx } from "@v0y4ge/jrni-ui";
import { Screen, PageHeader } from "../../shell/chrome";
import { ExplainerFooter } from "../../shell/composites";
import { DataSourceStrip } from "../../shell/opSource";
import { useNav } from "../../shell/router";
import { toast } from "../../shell/toast";
import { can } from "../../shell/access";
import { ChGlyph, ChannelChip, MSG_CHANNEL } from "./messaging-shared";
import { messagingTabs, useMessagingCounts } from "./messaging-tabs";
import {
  DELIV_EVENTS, DELIV_SOURCES, SOURCE_FILTER_LABELS,
  lastEvent, sortDeliveries, type DeliveryRow,
} from "./delivery-data";
import { fetchMessageHistory, fetchMessageHistoryDetail, itemToRow, detailToRow } from "./message-history-api";
import { exportDeliveryCsv } from "@v0y4ge/api-client";
import { useT } from "../../lib/i18n";

const IconMap = Icon as unknown as Record<string, (p?: { size?: number }) => ReactNode>;
const delivTh: CSSProperties = { textAlign: "left", padding: "10px 16px", whiteSpace: "nowrap" };
const delivTd: CSSProperties = { padding: "12px 16px", verticalAlign: "middle" };

const delivPill = (type: string) => { const e = DELIV_EVENTS[type] || DELIV_EVENTS.send; return <Pill tone={e.tone as any} dot>{e.label}</Pill>; };

function SourceTag({ source }: { source: { kind: string; label: string } }) {
  const s = DELIV_SOURCES[source.kind] || DELIV_SOURCES.manual;
  const I = IconMap[s.icon] || IconMap.send;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5, color: T.n1 }}>
      <span style={{ width: 20, height: 20, borderRadius: 5, background: alpha(s.color, "18"), color: s.color, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{I({ size: 11 })}</span>
      <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{source.label}</span>
    </span>
  );
}

interface SelOpt { value: string; label: string; }

/* Single-select on Popover + buttons.
 * ui-raw: bespoke single-select trigger + option-row buttons — jrni-ui's Dropdown
 * has no `width`/`align` (right-align used for the rows-per-page control below) and
 * always renders left-aligned/full-trigger-width, so swapping would change the
 * pagination control's position and menu sizing. Hand-rolled to preserve pixel parity. */
function DSelect({ value, options, onChange, width = 176, align = "left" }: { value: string | number; options: SelOpt[]; onChange: (v: any) => void; width?: number; align?: "left" | "right" }) {
  const [open, setOpen] = useState(false);
  const cur = options.find((o) => String(o.value) === String(value)) || options[0];
  return (
    <span style={{ position: "relative", display: "inline-flex" }}>
      {/* ui-raw: see file-header note on DSelect */}
      <button type="button" onClick={() => setOpen((o) => !o)} style={{ height: 40, width, display: "inline-flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "0 12px", border: `1px solid ${open ? T.p1 : T.n4}`, borderRadius: 8, background: T.white, color: T.n1, fontSize: 13, fontWeight: 500, fontFamily: T.font, cursor: "pointer", boxShadow: open ? `0 0 0 1px ${T.focus}` : "none" }}>
        <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{cur ? cur.label : ""}</span>
        <Icon.chev dir={open ? "up" : "down"} size={13} />
      </button>
      {open ? (
        <Popover open onClose={() => setOpen(false)} align={align} offset={6} width={width}>
          <div style={{ padding: 4, maxHeight: 300, overflow: "auto" }}>
            {options.map((o) => {
              const on = String(o.value) === String(value);
              return (
                // ui-raw: see file-header note on DSelect
                <button key={String(o.value)} onClick={() => { onChange(o.value); setOpen(false); }} style={{ display: "flex", width: "100%", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "8px 10px", border: "none", background: on ? T.p4 : "transparent", borderRadius: 6, cursor: "pointer", fontFamily: T.font, fontSize: 13, color: on ? T.p1 : T.n1, fontWeight: on ? 600 : 500, textAlign: "left" }}>
                  {o.label}{on ? <Icon.check size={13} /> : null}
                </button>
              );
            })}
          </div>
        </Popover>
      ) : null}
    </span>
  );
}

/* Multi-select on Popover + buttons — empty array = "all".
 * ui-raw: bespoke multi-select trigger + option-row buttons — jrni-ui's MultiSelect
 * renders picks as removable chips (or a fixed "N selected" summary), not this
 * screen's "All sources" / single-label / "N sources" trigger text, and has no
 * `align="right"`. Adopting it would visibly change the filter bar. Hand-rolled to
 * preserve pixel parity. */
function DMultiSelect({ value, options, onChange, allLabel, noun, width = 176, align = "left" }: { value: string[]; options: SelOpt[]; onChange: (v: string[]) => void; allLabel: string; noun: string; width?: number; align?: "left" | "right" }) {
  const [open, setOpen] = useState(false);
  const sel = value || [];
  const toggle = (v: string) => onChange(sel.includes(v) ? sel.filter((x) => x !== v) : [...sel, v]);
  const label = sel.length === 0 ? allLabel
    : sel.length === 1 ? ((options.find((o) => o.value === sel[0]) || {}).label || allLabel)
      : `${sel.length} ${noun}`;
  const activeBtn = sel.length > 0;
  const row = (label: ReactNode, on: boolean, onClick: () => void, key: string) => (
    // ui-raw: see file-header note on DMultiSelect
    <button key={key} onClick={onClick} style={{ display: "flex", width: "100%", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "8px 10px", border: "none", background: on ? T.p4 : "transparent", borderRadius: 6, cursor: "pointer", fontFamily: T.font, fontSize: 13, color: on ? T.p1 : T.n1, fontWeight: on ? 600 : 500, textAlign: "left" }}>
      {label}{on ? <Icon.check size={13} /> : null}
    </button>
  );
  return (
    <span style={{ position: "relative", display: "inline-flex" }}>
      {/* ui-raw: see file-header note on DMultiSelect */}
      <button type="button" onClick={() => setOpen((o) => !o)} style={{ height: 40, width, display: "inline-flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "0 12px", border: `1px solid ${open || activeBtn ? T.p1 : T.n4}`, borderRadius: 8, background: activeBtn ? T.p4 : T.white, color: T.n1, fontSize: 13, fontWeight: 500, fontFamily: T.font, cursor: "pointer", boxShadow: open ? `0 0 0 1px ${T.focus}` : "none" }}>
        <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{label}</span>
        <Icon.chev dir={open ? "up" : "down"} size={13} />
      </button>
      {open ? (
        <Popover open onClose={() => setOpen(false)} align={align} offset={6} width={Math.max(width, 200)}>
          <div style={{ padding: 4, maxHeight: 320, overflow: "auto" }}>
            {row(allLabel, sel.length === 0, () => onChange([]), "__all")}
            <div style={{ height: 1, background: T.border, margin: "4px 0" }} />
            {options.map((o) => row(o.label, sel.includes(o.value), () => toggle(o.value), String(o.value)))}
          </div>
        </Popover>
      ) : null}
    </span>
  );
}

// ui-raw: custom sortable <table>/<th> — jrni-ui's Table ships its own bordered/
// rounded wrapper (would double up with the Card below), a different two-arrow
// sort glyph, and a different row-hover treatment (tinted fill + inset accent
// bar vs this screen's plain background swap) — an exact-parity swap isn't
// available without visibly changing the header + row chrome.
function SortTh({ label, k, sort, setSort, style }: { label: string; k: string; sort: { key: string; dir: "asc" | "desc" }; setSort: (fn: (s: { key: string; dir: "asc" | "desc" }) => { key: string; dir: "asc" | "desc" }) => void; style?: CSSProperties }) {
  const active = sort.key === k;
  return (
    <th style={{ ...delivTh, ...style, cursor: "pointer", userSelect: "none" }}
      onClick={() => setSort((s) => s.key === k ? { key: k, dir: s.dir === "asc" ? "desc" : "asc" } : { key: k, dir: k === "latest" ? "desc" : "asc" })}>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 4, color: active ? T.n1 : "inherit" }}>
        {label}<span style={{ display: "inline-flex", opacity: active ? 1 : 0.28 }}><Icon.chev dir={active && sort.dir === "asc" ? "up" : "down"} size={11} /></span>
      </span>
    </th>
  );
}

function _pageList(page: number, count: number): (number | "…")[] {
  if (count <= 7) return Array.from({ length: count }, (_, i) => i + 1);
  const keep = [...new Set([1, 2, page - 1, page, page + 1, count - 1, count])].filter((p) => p >= 1 && p <= count).sort((a, b) => a - b);
  const res: (number | "…")[] = []; let prev = 0;
  for (const p of keep) { if (p - prev > 1) res.push("…"); res.push(p); prev = p; }
  return res;
}
// ui-raw: custom pagination — jrni-ui's Pagination primitive has no "Showing
// X–Y of Z" status text alongside a rows-per-page select, and its page-button
// chrome (inset radius/active-fill values) differs from this screen's. Composing
// it would still need this hand-rolled shell, so kept whole for parity.
function DeliveryPagination({ page, pageCount, total, start, end, pageSize, onPage, onPageSize }: { page: number; pageCount: number; total: number; start: number; end: number; pageSize: number; onPage: (p: number) => void; onPageSize: (n: number) => void }) {
  const t = useT();
  const PgBtn = ({ children, active, disabled, onClick, aria }: { children: ReactNode; active?: boolean; disabled?: boolean; onClick?: () => void; aria?: string }) => (
    <button type="button" aria-label={aria} disabled={disabled} onClick={onClick}
      style={{ minWidth: 30, height: 30, padding: "0 8px", borderRadius: 7, border: `1px solid ${active ? T.p1 : T.border}`, background: active ? T.p1 : T.white, color: active ? T.white : disabled ? T.n3 : T.n1, fontWeight: 600, fontSize: 12.5, fontFamily: T.font, cursor: disabled ? "default" : "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>{children}</button>
  );
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "10px 16px", borderTop: `1px solid ${T.border}`, flexWrap: "wrap" }}>
      <span style={{ fontSize: 12.5, color: T.soft }}>{t("messaging.deliveryLog.showing", "Showing")}{" "}<b style={{ color: T.n1 }}>{total ? start : 0}–{end}</b> {t("messaging.deliveryLog.of", "of")}{" "}<b style={{ color: T.n1 }}>{total}</b></span>
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        <PgBtn aria="Previous page" disabled={page <= 1} onClick={() => onPage(page - 1)}><Icon.chev dir="left" size={13} /></PgBtn>
        {_pageList(page, pageCount).map((p, i) => p === "…"
          ? <span key={"e" + i} style={{ padding: "0 4px", color: T.n3 }}>…</span>
          : <PgBtn key={p} active={p === page} onClick={() => onPage(p)}>{p}</PgBtn>)}
        <PgBtn aria="Next page" disabled={page >= pageCount} onClick={() => onPage(page + 1)}><Icon.chev dir="right" size={13} /></PgBtn>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 12.5, color: T.soft }}>{t("messaging.deliveryLog.rowsPerPage", "Rows per page")}</span>
        <DSelect value={pageSize} width={84} align="right" options={[{ value: "10", label: "10" }, { value: "25", label: "25" }, { value: "50", label: "50" }]} onChange={(v) => onPageSize(Number(v))} /> {/* ui-config */}
      </div>
    </div>
  );
}

export default function DeliveryLog() {
  const t = useT();
  const nav = useNav();
  const counts = useMessagingCounts();
  const [q, setQ] = useState("");
  const [chan, setChan] = useState<string[]>([]);
  const [status, setStatus] = useState<string[]>([]);
  const [src, setSrc] = useState<string[]>([]);
  const [openId, setOpenId] = useState<string | null>(null);
  const [sort, setSort] = useState<{ key: string; dir: "asc" | "desc" }>({ key: "latest", dir: "desc" });
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);

  const [all, setAll] = useState<DeliveryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [detail, setDetail] = useState<DeliveryRow | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Load the send log from listMessageHistory (limit 200 covers the full seed;
  // client-side filter/sort/paginate below stays as-is on the fetched set).
  useEffect(() => {
    let live = true;
    setLoading(true); setError(null);
    fetchMessageHistory({ limit: 200 })
      .then((resp) => { if (live) { setAll((resp.data || []).map(itemToRow)); setLoading(false); } })
      .catch((e) => { if (live) { setError(e?.message || "Failed to load the notification history."); setLoading(false); } });
    return () => { live = false; };
  }, []);

  // Full detail (timeline + rendered body) on drawer open.
  useEffect(() => {
    if (!openId) { setDetail(null); setDetailLoading(false); return; }
    let live = true;
    setDetail(null); setDetailLoading(true);
    fetchMessageHistoryDetail(openId)
      .then((d) => { if (live) { setDetail(detailToRow(d)); setDetailLoading(false); } })
      .catch(() => { if (live) setDetailLoading(false); });
    return () => { live = false; };
  }, [openId]);
  const baseFiltered = all.filter((d) => {
    if (chan.length && !chan.includes(d.channel)) return false;
    if (status.length && !status.includes(lastEvent(d).type)) return false;
    if (q.trim()) {
      const hay = `${d.recipient.name} ${d.recipient.to} ${d.sender.name} ${d.subject || d.text || ""} ${d.source.label}`.toLowerCase();
      if (!hay.includes(q.toLowerCase())) return false;
    }
    return true;
  });
  const filtered = src.length === 0 ? baseFiltered : baseFiltered.filter((d) => src.includes(d.source.kind));
  const rows = sortDeliveries(filtered, sort);

  const total = rows.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  useEffect(() => { setPage(1); }, [q, chan.join(), status.join(), src.join(), pageSize, sort.key, sort.dir]);
  const safePage = Math.min(page, pageCount);
  const startIdx = (safePage - 1) * pageSize;
  const paged = rows.slice(startIdx, startIdx + pageSize);

  const listRow = openId ? all.find((d) => d.id === openId) ?? null : null;
  const open = detail ?? listRow;
  const chanOpts = Array.from(new Set(all.map((d) => d.channel)));
  const chanSelOpts: SelOpt[] = chanOpts.map((c) => ({ value: c, label: MSG_CHANNEL(c).name }));
  const statusSelOpts: SelOpt[] = Object.keys(DELIV_EVENTS).map((k) => ({ value: k, label: DELIV_EVENTS[k].label }));
  const srcKinds = ["flow", "campaign", "trigger", "ticketgroup", "onsite", "manual"].filter((k) => all.some((d) => d.source.kind === k));
  const srcSelOpts: SelOpt[] = srcKinds.map((k) => ({ value: k, label: SOURCE_FILTER_LABELS[k] }));
  const filtersActive = q.trim() !== "" || chan.length > 0 || status.length > 0 || src.length > 0;
  const resetFilters = () => { setQ(""); setChan([]); setStatus([]); setSrc([]); };
  // Addendum item 9 (#262): Export awaits the real exportDeliveryCsv and
  // downloads the returned CSV — success feedback only from its receipt.
  const [exporting, setExporting] = useState(false);
  const doExport = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const r = await exportDeliveryCsv({ format: "csv", q: q.trim() || undefined }) as unknown as Record<string, unknown>;
      const body = (r.csv ?? r.content ?? r.body ?? (typeof r === "string" ? r : null)) as string | null;
      const url = typeof r.downloadUrl === "string" ? r.downloadUrl : body != null ? URL.createObjectURL(new Blob([body], { type: "text/csv" })) : null;
      if (!url) throw new Error("export returned no CSV payload");
      const a = document.createElement("a");
      a.href = url; a.download = String(r.filename || "delivery-log.csv"); a.rel = "noopener"; a.style.display = "none";
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      if (!String(r.downloadUrl || "")) setTimeout(() => URL.revokeObjectURL(url), 4000);
      toast({ tone: "success", title: t("messaging.deliveryLog.exportDownloaded", "Export downloaded"), subtitle: String(r.filename || "delivery-log.csv") });
    } catch (e) {
      toast({ tone: "danger", title: t("messaging.deliveryLog.exportFailed", "Export failed"), subtitle: e instanceof Error ? e.message : String(e) });
    } finally { setExporting(false); }
  };

  return (
    <Screen rail="messaging" tabs={messagingTabs(nav, "delivery", counts)} activeTab="delivery">
      <PageHeader
        title={t("messaging.deliveryLog.messaging", "Messaging")}
        subtitle={t("messaging.deliveryLog.deliveryEveryNotificationSentAcrossChannelsWithFullPerRecipientEventHist", "Delivery — every notification sent across channels, with full per-recipient event history.")}
        actions={<>
          {can("reports.export") ? <Btn variant="secondary" icon={<Icon.download size={14} />} disabled={exporting} onClick={() => void doExport()}>{exporting ? t("messaging.deliveryLog.exporting", "Exporting…") : t("common.actions.export", "Export")}</Btn> : null}
        </>}
      />
      <DataSourceStrip screen="messaging-delivery" ops={["listMessageHistory", "getMessageHistoryDetail"]} />
      <div style={{ flex: 1, overflow: "auto", padding: "16px 24px 28px", display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", paddingTop: 2, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 180 }}>
            <Input placeholder={t("messaging.deliveryLog.findByRecipientSenderSubject", "Find by recipient, sender, subject…")} value={q} onChange={(e) => setQ(e.target.value)} style={{ width: "100%" }} />
          </div>
          <DMultiSelect value={src} options={srcSelOpts} onChange={setSrc} allLabel="All sources" noun="sources" width={170} />
          <DMultiSelect value={chan} options={chanSelOpts} onChange={setChan} allLabel="All channels" noun="channels" width={158} />
          <DMultiSelect value={status} options={statusSelOpts} onChange={setStatus} allLabel="All statuses" noun="statuses" width={166} />
          {filtersActive ? (
            <Btn type="button" variant="link" size="sm" onClick={resetFilters} icon={<Icon.x size={12} />}
              style={{ flexShrink: 0, height: "auto", gap: 6, padding: "6px 10px", fontSize: 12.5, fontWeight: 600, whiteSpace: "nowrap" }}>
              {t("messaging.deliveryLog.clearFilters", "Clear filters")}
            </Btn>
          ) : null}
        </div>

        <Card style={{ padding: 0, marginTop: 14 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: T.font, fontSize: 13 }}>
            <thead>
              <tr style={{ background: T.n6, color: T.soft, fontSize: 11, textTransform: "uppercase", fontWeight: 600 }}>
                <SortTh label={t("messaging.deliveryLog.recipient", "Recipient")} k="recipient" sort={sort} setSort={setSort} />
                <SortTh label={t("messaging.deliveryLog.channel", "Channel")} k="channel" sort={sort} setSort={setSort} style={{ width: 100 }} />
                <SortTh label={t("messaging.deliveryLog.message", "Message")} k="message" sort={sort} setSort={setSort} />
                <SortTh label={t("messaging.deliveryLog.sender", "Sender")} k="sender" sort={sort} setSort={setSort} />
                <SortTh label={t("messaging.deliveryLog.source", "Source")} k="source" sort={sort} setSort={setSort} />
                <SortTh label={t("messaging.deliveryLog.latestEvent", "Latest event")} k="latest" sort={sort} setSort={setSort} style={{ width: 160 }} />
              </tr>
            </thead>
            <tbody>
              {paged.map((d, i) => {
                const le = lastEvent(d);
                return (
                  <tr key={d.id} onClick={() => setOpenId(d.id)} style={{ borderTop: i ? `1px solid ${T.border}` : "none", cursor: "pointer" }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = T.n6)} onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
                    <td style={delivTd}>
                      <div style={{ fontWeight: 600, color: T.n1 }}>{d.recipient.name}</div>
                      <div style={{ fontSize: 11.5, color: T.soft, fontFamily: T.fontMono }}>{d.recipient.to}</div>
                    </td>
                    <td style={delivTd}><ChannelChip id={d.channel} size="sm" /></td>
                    <td style={{ ...delivTd, maxWidth: 280 }}>
                      <div style={{ color: T.n1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.subject || d.text}</div>
                    </td>
                    <td style={delivTd}><div style={{ color: T.n1 }}>{d.sender.name}</div><div style={{ fontSize: 11.5, color: T.soft }}>{d.sender.from}</div></td>
                    <td style={{ ...delivTd, maxWidth: 200 }}><SourceTag source={d.source} /></td>
                    <td style={delivTd}>{delivPill(le.type)}<div style={{ fontSize: 11, color: T.soft, marginTop: 3 }}>{le.at}</div></td>
                  </tr>
                );
              })}
              {loading ? (
                <tr><td colSpan={6} style={{ padding: "40px 16px", textAlign: "center", color: T.soft }}>{t("messaging.deliveryLog.loadingNotificationHistory", "Loading notification history…")}</td></tr>
              ) : error ? (
                <tr><td colSpan={6} style={{ padding: "40px 16px", textAlign: "center", color: T.red1 }}>{error}</td></tr>
              ) : total === 0 ? (
                <tr><td colSpan={6} style={{ padding: "40px 16px", textAlign: "center", color: T.soft }}>{t("messaging.deliveryLog.noNotificationsMatchTheseFilters", "No notifications match these filters.")}</td></tr>
              ) : null}
            </tbody>
          </table>
          {total > 0 ? (
            <DeliveryPagination page={safePage} pageCount={pageCount} total={total}
              start={startIdx + 1} end={Math.min(startIdx + pageSize, total)} pageSize={pageSize}
              onPage={setPage} onPageSize={setPageSize} />
          ) : null}
        </Card>

        <ExplainerFooter>
          <b>{t("messaging.deliveryLog.theSendLog", "The send log.")}</b> {t("messaging.deliveryLog.oneRowPerNotificationThatWentOutAcrossEmailSmsAndWhatsapp", "One row per notification that went out — across email, SMS and WhatsApp, and across every source: flows, campaigns, ticket-tier flows, manual sends and on-site sends. Open any row for its full event history and the rendered message.")}</ExplainerFooter>
      </div>

      {open ? <DeliveryDetailSheet d={open} loading={detailLoading} onClose={() => setOpenId(null)} /> : null}
    </Screen>
  );
}

function DeliveryDetailSheet({ d, loading, onClose }: { d: DeliveryRow; loading?: boolean; onClose: () => void }) {
  const t = useT();
  const [showSource, setShowSource] = useState(false);
  useEffect(() => {
    const prev = document.body.style.overflow; document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = prev; document.removeEventListener("keydown", onKey); };
  }, [onClose]);

  const ch = MSG_CHANNEL(d.channel);
  const credBadge = d.credentialSource === "platform_fallback"
    ? <span title={t("messaging.deliveryLog.sentWithThePlatformSSharedCredentials", "Sent with the platform's shared credentials")} style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: T.orange1, background: alpha(T.orange1, 14), borderRadius: 4, padding: "1px 6px", verticalAlign: "middle" }}>{t("messaging.deliveryLog.platformKey", "Platform key")}</span>
    : d.credentialSource === "tenant"
      ? <span title={t("messaging.deliveryLog.sentWithYourOwnProviderCredentials", "Sent with your own provider credentials")} style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: T.green1, background: alpha(T.green1, 14), borderRadius: 4, padding: "1px 6px", verticalAlign: "middle" }}>{t("messaging.deliveryLog.ownKey", "Own key")}</span>
      : null;
  const providerLabel = d.provider ? d.provider.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : null;
  const meta: { label: string; value: ReactNode; sub?: string }[] = [
    { label: t("messaging.deliveryLog.to", "To"), value: <span style={{ fontFamily: T.fontMono }}>{d.recipient.to}</span>, sub: d.recipient.name },
    { label: t("messaging.deliveryLog.from", "From"), value: d.sender.name, sub: d.sender.from },
    { label: t("messaging.deliveryLog.source", "Source"), value: <SourceTag source={d.source} /> },
    { label: t("messaging.deliveryLog.messageId", "Message ID"), value: <span style={{ fontFamily: T.fontMono, fontSize: 12 }}>{d.displayId || d.id}</span> },
  ];
  if (providerLabel) meta.push({ label: t("messaging.deliveryLog.provider", "Provider"), value: <span>{providerLabel}{credBadge}</span> });
  if (d.replyTo) meta.push({ label: t("messaging.deliveryLog.replyTo", "Reply-to"), value: <span style={{ fontFamily: T.fontMono, fontSize: 12 }}>{d.replyTo}</span> });
  const toneColor: Record<string, string> = { neutral: T.n2, success: T.green1, info: T.blue1, primary: T.p1, warn: T.orange1, danger: T.red1 };
  const toneBg: Record<string, string> = { neutral: T.n6, success: T.green2, info: T.blue2, primary: T.p4, warn: T.orange2, danger: T.red2 };

  return (
    <div role="dialog" aria-label={t("messaging.deliveryLog.deliveryDetails", "Delivery details")} onClick={onClose}
      style={{ position: "fixed", inset: 0, zIndex: 110, display: "flex", justifyContent: "flex-end", background: "rgba(20,16,46,0.4)", animation: "delivFade 180ms cubic-bezier(0.4,0,0.2,1)" }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ width: 560, maxWidth: "94vw", height: "100%", background: T.white, boxShadow: T.shadowLg, display: "flex", flexDirection: "column", overflow: "hidden", animation: "delivSlide 220ms cubic-bezier(0.4,0,0.2,1)", fontFamily: T.font }}>
        <div style={{ padding: "16px 20px", borderBottom: `1px solid ${T.border}`, display: "flex", gap: 12, alignItems: "flex-start" }}>
          <span style={{ width: 34, height: 34, borderRadius: 8, flexShrink: 0, background: alpha(ch.hue, "18"), color: ch.hue, display: "inline-flex", alignItems: "center", justifyContent: "center" }}><ChGlyph id={d.channel} size={17} color={ch.hue} /></span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 4 }}>
              {delivPill(d.status || lastEvent(d).type)}
              <span style={{ fontSize: 11, color: T.soft }}>{d.channel === "email" ? t('messaging.deliveryLog.text') : ch.name} {t("messaging.deliveryLog.sent", "· sent")}{" "}{d.sent}</span>
            </div>
            <h2 style={{ margin: 0, fontSize: 16.5, fontWeight: 700, color: T.n1, lineHeight: 1.3 }}>{d.subject || d.text}</h2>
          </div>
          {/* ui-raw: borderless/transparent icon button — jrni-ui's IconButton always
              renders its own fixed border+chrome-shadow+background (not overridable
              via `style`, which the component discards in favor of its own literal),
              so it can't reproduce this flat, borderless close affordance. */}
          <button onClick={onClose} aria-label={t("common.actions.close", "Close")} style={{ width: 32, height: 32, borderRadius: 6, border: "none", background: "transparent", color: T.soft, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center" }}><Icon.x size={14} /></button>
        </div>

        <div style={{ flex: 1, overflow: "auto", padding: "16px 20px 28px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 22 }}>
            {meta.map((m, i) => (
              <div key={i}>
                <div style={{ ...tx.label, marginBottom: 4 }}>{m.label}</div>
                <div style={{ fontSize: 13.5, color: T.n1, fontWeight: 600 }}>{m.value}</div>
                {m.sub ? <div style={{ fontSize: 11.5, color: T.soft, marginTop: 1 }}>{m.sub}</div> : null}
              </div>
            ))}
          </div>

          <div style={{ ...tx.label, marginBottom: 12 }}>{t("messaging.deliveryLog.eventHistory", "Event history")}</div>
          {loading ? (
            <div style={{ fontSize: 12.5, color: T.soft, marginBottom: 24 }}>{t("messaging.deliveryLog.loadingEventHistory", "Loading event history…")}</div>
          ) : (
          <ol style={{ listStyle: "none", margin: "0 0 24px", padding: 0 }}>
            {d.events.map((ev, i) => {
              const e = DELIV_EVENTS[ev.type] || DELIV_EVENTS.send;
              const EI = IconMap[e.icon] || IconMap.send;
              const tone = toneColor[e.tone]; const bg = toneBg[e.tone];
              const last = i === d.events.length - 1;
              return (
                <li key={i} style={{ display: "grid", gridTemplateColumns: "28px 1fr", gap: 12, position: "relative" }}>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <span style={{ width: 28, height: 28, borderRadius: 999, background: bg, color: tone, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0, border: `1px solid ${alpha(tone, "33")}` }}>{EI({ size: 13 })}</span>
                    {!last ? <span style={{ width: 2, flex: 1, minHeight: 18, background: T.border, margin: "2px 0" }} /> : null}
                  </div>
                  <div style={{ paddingBottom: last ? 0 : 18 }}>
                    <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10 }}>
                      <span style={{ fontSize: 14, fontWeight: 700, color: T.n1 }}>{e.label}</span>
                      <span style={{ fontSize: 11.5, color: T.soft, fontFamily: T.fontMono, whiteSpace: "nowrap" }}>{ev.at}</span>
                    </div>
                    <div style={{ fontSize: 12.5, color: T.soft, marginTop: 3, lineHeight: 1.5 }}>{ev.meta || e.blurb}</div>
                  </div>
                </li>
              );
            })}
          </ol>
          )}

          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
            <div style={tx.label}>{t("messaging.deliveryLog.message", "Message")}</div>
            {d.channel === "email" && !loading && !d.bodyUnavailable ? (
              // ui-raw: 2-way Rendered/HTML-source toggle — jrni-ui's SegmentedControl
              // renders an inset track (padded n6 fill + p3 active chip) rather than
              // this flush bordered-pill pair with a divider between segments; adopting
              // it would visibly change the toggle's chrome.
              <span style={{ display: "inline-flex", borderRadius: 7, border: `1px solid ${T.border}`, overflow: "hidden" }}>
                {[{ k: false, l: "Rendered" }, { k: true, l: "HTML source" }].map((o, idx) => (
                  <button key={o.l} onClick={() => setShowSource(o.k)} style={{ padding: "5px 11px", border: "none", borderLeft: idx ? `1px solid ${T.border}` : "none", background: showSource === o.k ? T.p4 : T.white, color: showSource === o.k ? T.p1 : T.soft, fontWeight: 600, fontSize: 12, cursor: "pointer", fontFamily: T.font }}>{o.l}</button>
                ))}
              </span>
            ) : null}
          </div>

          {loading ? (
            <div style={{ padding: 24, textAlign: "center", color: T.soft, fontSize: 13, border: `1px solid ${T.border}`, borderRadius: 10 }}>{t("messaging.deliveryLog.loadingMessage", "Loading message…")}</div>
          ) : d.bodyUnavailable ? (
            <div style={{ padding: 20, border: `1px dashed ${T.n4}`, borderRadius: 10, background: T.n6, color: T.soft, fontSize: 13, display: "flex", alignItems: "center", gap: 8 }}><Icon.warn size={14} /> {t("messaging.deliveryLog.messageBodyUnavailableSuppressedOrBouncedSendsArenTStored", "Message body unavailable — suppressed or bounced sends aren't stored.")}</div>
          ) : d.channel === "email" ? (
            showSource ? (
              // ui-raw: fixed dark code-panel palette (#1c1830/#d7d2f0) — an intentional
              // always-dark HTML-source viewer independent of the app's light/dark theme,
              // not a themed surface with a matching T.* token.
              <pre style={{ margin: 0, padding: 14, background: "#1c1830", color: "#d7d2f0", borderRadius: 10, fontSize: 11.5, fontFamily: T.fontMono, lineHeight: 1.5, overflow: "auto", maxHeight: 460, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{d.html}</pre>
            ) : (
              <div style={{ border: `1px solid ${T.border}`, borderRadius: 10, overflow: "hidden", background: T.canvas }}>
                {/* ui-raw: #f4f5f7 is the iframe's own default-white-page background
                    (mirrors an email client's letterbox), not an app surface token. */}
                <iframe title={t("messaging.deliveryLog.emailPreview", "Email preview")} srcDoc={d.html} sandbox="" style={{ width: "100%", height: 480, border: "none", display: "block", background: "#f4f5f7" }} />
              </div>
            )
          ) : (
            <div style={{ background: alpha(ch.hue, "0E"), border: `1px solid ${alpha(ch.hue, "33")}`, borderRadius: 12, padding: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                <span style={{ width: 26, height: 26, borderRadius: 999, background: alpha(ch.hue, "20"), color: ch.hue, display: "inline-flex", alignItems: "center", justifyContent: "center" }}><ChGlyph id={d.channel} size={13} color={ch.hue} /></span>
                <span style={{ fontSize: 12, fontWeight: 700, color: T.n1 }}>{d.sender.name}</span>
                <span style={{ fontSize: 11, color: T.soft }}>{d.sender.from}</span>
              </div>
              <div style={{ background: T.white, border: `1px solid ${alpha(ch.hue, "26")}`, borderRadius: "4px 14px 14px 14px", padding: "12px 14px", fontSize: 14, lineHeight: 1.55, color: T.n1, maxWidth: 380 }}>{d.text}</div>
            </div>
          )}
        </div>

        <div style={{ borderTop: `1px solid ${T.border}`, padding: "12px 20px", display: "flex", justifyContent: "flex-end", gap: 8, background: T.n6 }}>
          <Btn size="sm" onClick={onClose}>{t("common.actions.close", "Close")}</Btn>
        </div>
      </div>
      <style>{t('messaging.deliveryLog.text2')}</style>
    </div>
  );
}
